import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import styles from './MatchExternalTransactionModal.module.css';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { toast } from 'react-toastify';
import { formatCurrency } from '../util/formatCurrency';
import {
  ExternalFinancialTransaction,
  ExternalFinancialTransactionMatch,
  OrderFinancialTransactionSuggestion,
} from '../types';

interface MatchExternalTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChange: () => void;
  externalTransaction: ExternalFinancialTransaction | null;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return 'N/A';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const date = new Date(isDateOnly ? `${dateString}T00:00:00` : dateString);
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
};

const formatSignedAmount = (amount: string | undefined) => {
  if (!amount) return '';
  const value = parseFloat(amount);
  if (isNaN(value)) return amount;
  const formatted = formatCurrency(Math.abs(value));
  return value < 0 ? `-${formatted}` : formatted;
};

const MatchExternalTransactionModal: React.FC<MatchExternalTransactionModalProps> = ({
  isOpen,
  onClose,
  onChange,
  externalTransaction,
}) => {
  const [matches, setMatches] = useState<ExternalFinancialTransactionMatch[]>([]);
  const [suggestions, setSuggestions] = useState<OrderFinancialTransactionSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingMatch, setPendingMatch] =
    useState<{ suggestion: OrderFinancialTransactionSuggestion; amount: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const txnId = externalTransaction?.id ?? null;

  const fetchSuggestions = useCallback(
    async (q: string) => {
      if (txnId == null) return;
      setLoadingSuggestions(true);
      try {
        const query = new URLSearchParams();
        if (q.trim()) query.append('q', q.trim());
        const res = await fetchWithAuth(
          `/external_financial_transactions/${txnId}/match_suggestions${
            query.toString() ? `?${query.toString()}` : ''
          }`
        );
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (error) {
        console.error('Error fetching match suggestions:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    },
    [txnId]
  );

  useEffect(() => {
    if (isOpen && externalTransaction) {
      setMatches(externalTransaction.matches || []);
      setPendingMatch(null);
      setSearchTerm('');
      fetchSuggestions('');
    }
  }, [isOpen, externalTransaction, fetchSuggestions]);

  useEffect(() => {
    if (!isOpen || searchTerm === '') return;
    const handle = setTimeout(() => {
      fetchSuggestions(searchTerm);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm, isOpen, fetchSuggestions]);

  if (!externalTransaction) {
    return null;
  }

  const externalAmount = parseFloat(externalTransaction.amount);
  const matchSum = matches.reduce((acc, m) => acc + parseFloat(m.amount || '0'), 0);
  const sumsBalance = !isNaN(externalAmount) && Math.abs(matchSum - externalAmount) < 0.005;

  const handleUnmatch = async (matchId: number) => {
    try {
      const res = await fetchWithAuth(
        `/external_financial_transactions/${externalTransaction.id}/matches/${matchId}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setMatches((prev) => prev.filter((m) => m.id !== matchId));
        toast.success('Match removed');
        onChange();
        fetchSuggestions(searchTerm);
      } else {
        toast.error('Failed to remove match');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    }
  };

  const handleConfirmMatch = async () => {
    if (!pendingMatch) return;
    const amountValue = pendingMatch.amount.trim();
    if (!amountValue || isNaN(Number(amountValue)) || Number(amountValue) === 0) {
      toast.error('Amount must be a non-zero number');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `/external_financial_transactions/${externalTransaction.id}/matches`,
        {
          method: 'POST',
          body: JSON.stringify({
            external_financial_transaction_match: {
              order_financial_transaction_id: pendingMatch.suggestion.id,
              amount: amountValue,
            },
          }),
        }
      );

      if (res.ok) {
        const created = await res.json();
        setMatches((prev) => [...prev, created]);
        setPendingMatch(null);
        toast.success('Match added');
        onChange();
        fetchSuggestions(searchTerm);
      } else if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        const message =
          (data.errors && (data.errors.base?.[0] || Object.values(data.errors)[0]?.toString())) ||
          'Could not create match';
        toast.error(message);
      } else {
        toast.error('Failed to create match');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <div className="flex items-start justify-between mb-4">
        <h2 className="text-2xl font-bold">Match Transaction</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-sm">
          <div>
            <div className="text-xs uppercase text-gray-500">Date</div>
            <div className="text-gray-900">{formatDate(externalTransaction.date)}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs uppercase text-gray-500">Description</div>
            <div className="text-gray-900 break-words">{externalTransaction.description}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-gray-500">Amount</div>
            <div className={externalAmount < 0 ? 'text-red-600' : 'text-gray-900'}>
              {formatSignedAmount(externalTransaction.amount)}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-gray-500">Source</div>
            <div className="text-gray-900">{externalTransaction.finance_source_label}</div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span className="text-xs uppercase text-gray-500">Category: </span>
          <span className="text-gray-900">{externalTransaction.category_label || '—'}</span>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Existing Matches</h3>
        {matches.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No matches yet</p>
        ) : (
          <ul className="divide-y border border-gray-200 rounded">
            {matches.map((m) => (
              <li
                key={m.id}
                className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm"
              >
                <span className="text-gray-600">{formatDate(m.oft_date)}</span>
                <span className="font-medium text-gray-900">
                  {m.customer_name || m.customer_code || `Customer #${m.customer_id ?? '?'}`}
                </span>
                {m.customer_code && (
                  <span className="text-gray-500">{m.customer_code}</span>
                )}
                {m.order_reference && (
                  <span className="text-blue-600">Order {m.order_reference}</span>
                )}
                <span className="ml-auto text-gray-700 text-xs">
                  OFT: {formatSignedAmount(m.oft_amount)}
                </span>
                <span className="font-mono">{formatSignedAmount(m.amount)}</span>
                <button
                  type="button"
                  onClick={() => handleUnmatch(m.id)}
                  className="ml-2 text-red-600 hover:text-red-800 text-xs"
                >
                  Unmatch
                </button>
              </li>
            ))}
          </ul>
        )}
        <div
          className={`mt-3 text-sm rounded px-3 py-2 ${
            sumsBalance ? 'bg-green-50 text-green-800' : 'bg-orange-50 text-orange-800'
          }`}
          data-testid="reconciliation-summary"
        >
          Reconciliation: {formatSignedAmount(matchSum.toFixed(2))} of{' '}
          {formatSignedAmount(externalTransaction.amount)}{' '}
          {sumsBalance ? '✓ balanced' : '⚠ mismatch'}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Add Match</h3>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by customer name, code or order reference..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
        />

        {loadingSuggestions ? (
          <p className="text-sm text-gray-500">Loading suggestions...</p>
        ) : suggestions.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No suggestions found</p>
        ) : (
          <ul className="divide-y border border-gray-200 rounded max-h-80 overflow-y-auto">
            {suggestions.map((s) => (
              <li
                key={s.id}
                className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm"
              >
                <span className="text-gray-600">{formatDate(s.date)}</span>
                <span className="font-medium text-gray-900">
                  {s.customer_name || s.customer_code || `Customer #${s.customer_id ?? '?'}`}
                </span>
                {s.customer_code && (
                  <span className="text-gray-500">{s.customer_code}</span>
                )}
                {s.order_reference && (
                  <span className="text-blue-600">Order {s.order_reference}</span>
                )}
                <span className="text-gray-700">{s.description}</span>
                <span className="ml-auto font-mono">{formatSignedAmount(s.amount)}</span>
                <button
                  type="button"
                  onClick={() => setPendingMatch({ suggestion: s, amount: s.amount })}
                  className="ml-2 bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
                >
                  Match
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pendingMatch && (
        <div
          className="mt-4 border border-blue-200 bg-blue-50 rounded p-3"
          data-testid="pending-match"
        >
          <div className="text-sm font-semibold mb-2">
            Match amount for{' '}
            {pendingMatch.suggestion.customer_name || pendingMatch.suggestion.customer_code}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="decimal"
              value={pendingMatch.amount}
              onChange={(e) =>
                setPendingMatch((p) => (p ? { ...p, amount: e.target.value } : p))
              }
              className="flex-1 px-3 py-2 border border-gray-300 rounded"
              aria-label="Match amount"
            />
            <button
              type="button"
              onClick={handleConfirmMatch}
              disabled={isSubmitting}
              className="bg-blue-500 text-white px-3 py-2 rounded disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={() => setPendingMatch(null)}
              className="bg-gray-200 text-gray-700 px-3 py-2 rounded"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button
          type="button"
          onClick={onClose}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
        >
          Done
        </button>
      </div>
    </Modal>
  );
};

export default MatchExternalTransactionModal;
